import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  addDoc, 
  updateDoc, 
  doc,
  getDoc,
  serverTimestamp,
  limit,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { sendReportReviewedNotification } from '../../emailjs/emailService';
import { createNotification } from './notificationSlice';
import { format, getMonth, getYear, startOfWeek, endOfWeek } from 'date-fns';

// Collection references
const progressReportsRef = collection(db, 'progressReports');
const monthlyScoresRef = collection(db, 'monthlyScores');

// Helper to convert Firebase timestamps to JS Date objects
const convertTimestamps = (data) => {
  const result = { ...data };
  
  // Convert common timestamp fields
  const timestampFields = ['createdAt', 'submittedAt', 'reviewedAt', 'weekStartDate', 'weekEndDate'];
  
  timestampFields.forEach(field => {
    if (result[field] && result[field].seconds) {
      // Convert Firebase timestamp to JS Date
      result[field] = new Date(result[field].seconds * 1000);
    }
  });
  
  return result;
};

// Fetch user progress reports
export const fetchUserReports = createAsyncThunk(
  'performanceReview/fetchUserReports',
  async (email, { rejectWithValue }) => {
    try {
      const reportsQuery = query(
        progressReportsRef,
        where('email', '==', email),
        orderBy('weekStartDate', 'desc')
      );
      
      const snapshot = await getDocs(reportsQuery);
      
      if (snapshot.empty) {
        return [];
      }
      
      const reports = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...convertTimestamps(data)
        };
      });
      
      return reports;
    } catch (error) {
      console.error('Error fetching user reports:', error);
      return rejectWithValue(error.message || 'Failed to fetch reports');
    }
  }
);

// Fetch monthly scores
export const fetchMonthlyScores = createAsyncThunk(
  'performanceReview/fetchMonthlyScores',
  async (email, { rejectWithValue }) => {
    try {
      const scoresQuery = query(
        monthlyScoresRef,
        where('email', '==', email),
        orderBy('year', 'desc'),
        orderBy('month', 'desc'),
        limit(3)
      );
      
      const snapshot = await getDocs(scoresQuery);
      
      if (snapshot.empty) {
        return [];
      }
      
      const scores = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...convertTimestamps(data)
        };
      });
      
      return scores;
    } catch (error) {
      console.error('Error fetching monthly scores:', error);
      return rejectWithValue(error.message || 'Failed to fetch monthly scores');
    }
  }
);

// Submit or update a progress report
export const submitProgressReport = createAsyncThunk(
  'performanceReview/submitProgressReport',
  async ({ reportData, isUpdate, reportId }, { rejectWithValue }) => {
    try {
      const preparedData = {
        ...reportData,
        status: 'pending',
        submittedAt: serverTimestamp()
      };
      
      // Handle date fields - convert to Firebase timestamps
      if (preparedData.weekStartDate && !(preparedData.weekStartDate.seconds)) {
        preparedData.weekStartDate = Timestamp.fromDate(new Date(preparedData.weekStartDate));
      }
      
      if (preparedData.weekEndDate && !(preparedData.weekEndDate.seconds)) {
        preparedData.weekEndDate = Timestamp.fromDate(new Date(preparedData.weekEndDate));
      }
      
      if (isUpdate && reportId) {
        const reportRef = doc(db, 'progressReports', reportId);
        await updateDoc(reportRef, preparedData);
        
        // Get updated document
        const updatedDoc = await getDoc(reportRef);
        const updatedData = updatedDoc.data();
        
        return {
          id: reportId,
          ...convertTimestamps(updatedData),
          submittedAt: new Date() // Use client date for immediate UI update
        };
      } else {
        const docRef = await addDoc(progressReportsRef, preparedData);
        
        return {
          id: docRef.id,
          ...reportData,
          submittedAt: new Date() // Use client date for immediate UI update
        };
      }
    } catch (error) {
      console.error('Error submitting progress report:', error);
      return rejectWithValue(error.message || 'Failed to submit report');
    }
  }
);

// Fetch all reports for a specific employee (admin function)
export const fetchAllEmployeeReports = createAsyncThunk(
  'performanceReview/fetchAllEmployeeReports',
  async (email, { rejectWithValue }) => {
    try {
      const reportsQuery = query(
        progressReportsRef,
        where('email', '==', email),
        orderBy('weekStartDate', 'desc')
      );
      
      const snapshot = await getDocs(reportsQuery);
      
      if (snapshot.empty) {
        return { email, reports: [] };
      }
      
      const reports = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...convertTimestamps(data)
        };
      });
      
      return { email, reports };
    } catch (error) {
      console.error('Error fetching employee reports:', error);
      return rejectWithValue(error.message || 'Failed to fetch employee reports');
    }
  }
);

// Submit review for an employee's report
export const submitReview = createAsyncThunk(
  'performanceReview/submitReview',
  async ({ reportId, reviewData, employeeEmail, employeeName, reviewerName, reviewerEmail }, 
    { dispatch, rejectWithValue }) => {
    try {
      const reportRef = doc(db, 'progressReports', reportId);
      
      // Update report with review
      await updateDoc(reportRef, {
        score: reviewData.score,
        feedback: reviewData.feedback,
        status: 'reviewed',
        reviewedAt: serverTimestamp(),
        reviewer: reviewerEmail
      });
      
      // Get the report to access dates for monthly score update
      const reportSnapshot = await getDoc(reportRef);
      
      if (!reportSnapshot.exists()) {
        return rejectWithValue('Report not found');
      }
      
      const reportData = reportSnapshot.data();
      
      // Update monthly scores
      await updateMonthlyScore(
        employeeEmail, 
        reportData.weekStartDate, 
        Number(reviewData.score)
      );
      
      // Create notification for the employee
      dispatch(createNotification({
        title: 'Performance Report Reviewed',
        message: `Your performance report has been reviewed with a score of ${reviewData.score}.`,
        type: 'review',
        userEmail: employeeEmail,
        link: '/performance-review',
        isRead: false
      }));
      
      // Send email notification to the employee
      try {
        // Format dates for the email
        const weekStartDate = reportData.weekStartDate?.seconds 
          ? new Date(reportData.weekStartDate.seconds * 1000) 
          : new Date();
          
        const weekEndDate = reportData.weekEndDate?.seconds 
          ? new Date(reportData.weekEndDate.seconds * 1000) 
          : new Date();
        
        const formattedStartDate = format(weekStartDate, 'MMM d');
        const formattedEndDate = format(weekEndDate, 'MMM d, yyyy');
        
        // Prepare data for email
        const employeeData = {
          name: employeeName,
          email: employeeEmail
        };
        
        const reviewDetails = {
          score: reviewData.score,
          feedback: reviewData.feedback,
          reviewerName: reviewerName,
          reviewerEmail: reviewerEmail,
          weekStart: formattedStartDate,
          weekEnd: formattedEndDate
        };
        
        // Send the email notification
        const emailResult = await sendReportReviewedNotification(
          employeeData, 
          reviewDetails
        );
        
        console.log('Report reviewed email sent:', emailResult);
      } catch (emailError) {
        // Log error but don't fail the entire operation
        console.error('Error sending report reviewed email:', emailError);
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error submitting review:', error);
      return rejectWithValue(error.message || 'Failed to submit review');
    }
  }
);

// Send Weekly Performance Report Reminder
export const sendWeeklyReportReminder = createAsyncThunk(
  'performanceReview/sendWeeklyReportReminder',
  async (employees, { dispatch, rejectWithValue }) => {
    try {
      // Get the current week start/end dates
      const currentWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
      const currentWeekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
      
      // Create a batch of operations to create blank reports
      const batch = [];
      
      // For each selected employee, check if they already have a report for this week
      for (const employee of employees) {
        // Skip if no email
        if (!employee.email) continue;
        
        // Check if a report already exists for this week and employee
        const existingReportQuery = query(
          progressReportsRef,
          where('email', '==', employee.email),
          where('weekStartDate', '==', Timestamp.fromDate(currentWeekStart))
        );
        
        const existingReportSnapshot = await getDocs(existingReportQuery);
        
        // Only create a blank report if one doesn't already exist
        if (existingReportSnapshot.empty) {
          // Create a new blank report for this employee
          const blankReport = {
            email: employee.email,
            weekStartDate: Timestamp.fromDate(currentWeekStart),
            weekEndDate: Timestamp.fromDate(currentWeekEnd),
            status: 'blank',
            createdAt: serverTimestamp(),
            objectives: '',
            achievements: '',
            proudAchievements: '',
            measurableAchievements: '',
            improvements: '',
            feedback: ''
          };
          
          // Add to batch of operations
          batch.push(addDoc(progressReportsRef, blankReport));
          
          // Create notification for the employee
          dispatch(createNotification({
            title: 'Weekly Performance Report Reminder',
            message: `Please submit your performance report for the week of ${format(currentWeekStart, 'MMM d')} - ${format(currentWeekEnd, 'MMM d')}.`,
            type: 'reminder',
            userEmail: employee.email,
            link: '/performance-review',
            isRead: false
          }));
        }
      }
      
      // Execute all operations
      await Promise.all(batch);
      
      return { success: true, employeeCount: employees.length };
    } catch (error) {
      console.error('Error sending Weekly Performance Report Reminders:', error);
      return rejectWithValue('Failed to send Weekly Performance Report Reminders. Please try again.');
    }
  }
);

// Helper function to update monthly scores
const updateMonthlyScore = async (email, reportDate, newScore) => {
  // Convert Firebase timestamp to Date if needed
  const date = reportDate.seconds 
    ? new Date(reportDate.seconds * 1000) 
    : new Date(reportDate);
  
  const month = getMonth(date) + 1; // 1-12
  const year = getYear(date);
  
  try {
    // Check if monthly score record exists
    const monthlyScoreQuery = query(
      monthlyScoresRef,
      where('email', '==', email),
      where('month', '==', month),
      where('year', '==', year)
    );
    
    const monthlyScoreSnapshot = await getDocs(monthlyScoreQuery);
    
    if (monthlyScoreSnapshot.empty) {
      // Create new monthly score record
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0);
      
      await addDoc(monthlyScoresRef, {
        email,
        month,
        year,
        reportCount: 1,
        averageScore: newScore,
        startDate: Timestamp.fromDate(startDate),
        endDate: Timestamp.fromDate(endDate),
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    } else {
      // Update existing monthly score record
      const scoreDoc = monthlyScoreSnapshot.docs[0];
      const scoreData = scoreDoc.data();
      
      const totalScore = (scoreData.averageScore * scoreData.reportCount) + newScore;
      const newReportCount = scoreData.reportCount + 1;
      const newAverageScore = totalScore / newReportCount;
      
      const scoreDocRef = doc(monthlyScoresRef, scoreDoc.id);
      await updateDoc(scoreDocRef, {
        reportCount: newReportCount,
        averageScore: newAverageScore,
        lastUpdated: serverTimestamp()
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error updating monthly score:', error);
    return false;
  }
};

// Slice
const performanceReviewSlice = createSlice({
  name: 'performanceReview',
  initialState: {
    userReports: [],
    monthlyScores: [],
    employeeReports: {},
    selectedReport: null,
    loading: false,
    error: null,
    submitLoading: false,
    submitError: null,
    submitSuccess: false,
    reviewLoading: false,
    reviewError: null,
    reviewSuccess: false,
    reminderLoading: false,
    reminderError: null,
    reminderSuccess: false
  },
  reducers: {
    setSelectedReport: (state, action) => {
      state.selectedReport = action.payload;
    },
    clearErrors: (state) => {
      state.error = null;
      state.submitError = null;
      state.reviewError = null;
      state.reminderError = null;
    },
    clearSuccess: (state) => {
      state.submitSuccess = false;
      state.reviewSuccess = false;
      state.reminderSuccess = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchUserReports
      .addCase(fetchUserReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserReports.fulfilled, (state, action) => {
        state.loading = false;
        state.userReports = action.payload;
      })
      .addCase(fetchUserReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // fetchMonthlyScores
      .addCase(fetchMonthlyScores.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMonthlyScores.fulfilled, (state, action) => {
        state.loading = false;
        state.monthlyScores = action.payload;
      })
      .addCase(fetchMonthlyScores.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // submitProgressReport
      .addCase(submitProgressReport.pending, (state) => {
        state.submitLoading = true;
        state.submitError = null;
        state.submitSuccess = false;
      })
      .addCase(submitProgressReport.fulfilled, (state, action) => {
        state.submitLoading = false;
        state.submitSuccess = true;
      })
      .addCase(submitProgressReport.rejected, (state, action) => {
        state.submitLoading = false;
        state.submitError = action.payload;
      })
      
      // fetchAllEmployeeReports
      .addCase(fetchAllEmployeeReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllEmployeeReports.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeReports = {
          ...state.employeeReports,
          [action.payload.email]: action.payload.reports
        };
      })
      .addCase(fetchAllEmployeeReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // submitReview
      .addCase(submitReview.pending, (state) => {
        state.reviewLoading = true;
        state.reviewError = null;
        state.reviewSuccess = false;
      })
      .addCase(submitReview.fulfilled, (state) => {
        state.reviewLoading = false;
        state.reviewSuccess = true;
      })
      .addCase(submitReview.rejected, (state, action) => {
        state.reviewLoading = false;
        state.reviewError = action.payload;
      })
      
      // sendWeeklyReportReminder
      .addCase(sendWeeklyReportReminder.pending, (state) => {
        state.reminderLoading = true;
        state.reminderError = null;
        state.reminderSuccess = false;
      })
      .addCase(sendWeeklyReportReminder.fulfilled, (state) => {
        state.reminderLoading = false;
        state.reminderSuccess = true;
      })
      .addCase(sendWeeklyReportReminder.rejected, (state, action) => {
        state.reminderLoading = false;
        state.reminderError = action.payload;
      });
  }
});

export const { setSelectedReport, clearErrors, clearSuccess } = performanceReviewSlice.actions;
export default performanceReviewSlice.reducer; 